import React, { useReducer, useMemo, createContext, useContext } from 'react'
import { useBrandContext } from '@vp/react-brand'
import { schemesReducer, initialState } from './reducer'
import { Action, initializeActions, SchemeActionSet } from './actions'
import { SchemesStore } from './types'
import { useIdentity } from '@vp/ubik-context'

export interface StoreContextType extends SchemeActionSet {
  state: SchemesStore
  dispatch: React.Dispatch<Action>
}

export const SchemesStoreContext = createContext<StoreContextType | undefined>(undefined)

export const useSchemesStore = (): StoreContextType => {
  const context = useContext(SchemesStoreContext)

  if (!context) {
    throw new Error('useSchemesStore must be used within a PersonalizationStoreProvider')
  }
  return context
}

export const SchemesProvider: React.FC<{ children: React.ReactNode; defaultToPhoto?: boolean }> = ({ children, defaultToPhoto = false }) => {
  const [state, dispatch] = useReducer(schemesReducer, initialState)

  const brandContext = useBrandContext()
  const { identity } = useIdentity()
  const accessToken = identity?.accessToken || ''

  const actions = useMemo(
    () =>
      initializeActions({
        state,
        dispatch,
        brandContext,
        accessToken,
        defaultToPhoto,
      }),
    [state, dispatch, brandContext, accessToken, defaultToPhoto],
  )

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      ...actions,
    }),
    [state, dispatch, actions],
  )

  return <SchemesStoreContext.Provider value={contextValue}>{children}</SchemesStoreContext.Provider>
}
